"use client";

import { useMemo, type ReactNode } from "react";
import Link from "next/link";
import { PortableText } from "@portabletext/react";
import { Tag } from "@/components/ui/tag";

const bottomTextComponents = {
  marks: {
    underline: ({ children }: any) => <span style={{ textDecoration: "underline" }}>{children}</span>,
    white: ({ children }: any) => <span style={{ color: "#FFFFFF" }}>{children}</span>,
    red: ({ children }: any) => <span style={{ color: "#F74B48" }}>{children}</span>,
    green: ({ children }: any) => <span style={{ color: "#A8FBBE" }}>{children}</span>,
    link: ({ children, value }: any) => (
      <a
        href={value?.href}
        target={value?.isExternal ? "_blank" : undefined}
        rel={value?.isExternal ? "noopener noreferrer" : undefined}
        className="underline"
      >
        {children}
      </a>
    ),
  },
};


function GradientTextParser({ text }: { text: string }) {
  return useMemo(() => {
    if (!text) return null;
    const lines = text.split("\n");
    return lines.map((line, lineIndex) => {
      const parts = line.split(/(`[^`]*`)|(\*[^*]*\*)/g).filter(Boolean);
      return (
        <span key={lineIndex}>
          {lineIndex > 0 && <br />}
          {parts.map((part, index) => {
            if (part.startsWith("`") && part.endsWith("`")) {
              const inner = part.slice(1, -1);
              const isItalic = inner.startsWith("*") && inner.endsWith("*");
              const content = isItalic ? inner.slice(1, -1) : inner;
              return (
                <span
                  key={index}
                  className={`bg-[url('/images/footer_gradient-low-res.webp')] text-transparent bg-center bg-clip-text bg-cover bg-no-repeat${isItalic ? " italic" : ""}`}
                >
                  {content}
                </span>
              );
            }
            if (part.startsWith("*") && part.endsWith("*")) {
              return <em key={index}>{part.slice(1, -1)}</em>;
            }
            return part;
          })}
        </span>
      );
    });
  }, [text]);
}

type PillGroup = { label?: string; pills?: PillItem[]; _key?: string };

type Foundation = {
  superTag?: string;
  name?: string;
  description?: string;
  footnote?: string;
  color?: string | { value: string };
  pillGroups?: PillGroup[];
  link?: string;
};

type PillItem = string | { label: string; badge?: "new" | "coming-soon" };

function parsePill(pill: PillItem): { label: string; badge?: "new" | "coming-soon" } {
  if (typeof pill !== "string") return { label: pill.label, badge: pill.badge };
  if (pill.endsWith(" [NEW]")) return { label: pill.slice(0, -6), badge: "new" };
  if (pill.endsWith(" [COMING SOON]")) return { label: pill.slice(0, -14), badge: "coming-soon" };
  return { label: pill };
}

type Pillar = {
  eyebrow?: string;
  name?: string;
  tagline?: string;
  color?: string;
  pills?: PillItem[];
  link?: string;
  _key?: string;
};

function extractRgb(color: string): string | null {
  const match = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
  if (!match) return null;
  return `${match[1]}, ${match[2]}, ${match[3]}`;
}

export interface ArchitecturePillarsProps {
  tag?: string;
  heading?: string;
  subHeading?: string;
  pillars?: Pillar[];
  foundation?: Foundation;
  bottomText?: any[];
  bottomTextContent?: ReactNode;
  paddingTop?: string;
  paddingBottom?: string;
}

function PillarCard({ pillar }: { pillar: Pillar }) {
  const colorValue = (
    typeof pillar.color === "object" && pillar.color !== null
      ? (pillar.color as any).value
      : pillar.color
  ) || null;
  const rgb = colorValue ? extractRgb(colorValue) : null;
  const color = colorValue;

  const inner = (
    <div
      className="relative rounded-lg overflow-hidden flex flex-col h-full bg-grey-900 transition-transform duration-200 hover:-translate-y-0.5 p-5 md:p-6"
      style={{
        border: rgb
          ? `1px solid rgba(${rgb}, 0.2)`
          : "1px solid #2C2C2C",
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={
          color
            ? { background: color }
            : { backgroundImage: "url('/images/footer_gradient-low-res.webp')", backgroundSize: "cover", backgroundPosition: "center" }
        }
      />
      {pillar.eyebrow && (
        <p
          className="text-xxs font-mono font-bold tracking-[0.14em] uppercase mb-2.5 m-0"
          style={{ color: color ?? undefined }}
        >
          {pillar.eyebrow}
        </p>
      )}
      {pillar.name && (() => {
        const { label: nameLabel, badge: nameBadge } = parsePill(pillar.name);
        const pillRgbN = rgb ?? "247, 75, 72";
        return (
          <div className="flex items-center gap-2 mb-1.5">
            <h3
              className="text-[22px] font-extrabold text-white m-0 leading-none"
              style={{ letterSpacing: "-0.03em", fontFamily: "var(--font-roobert)" }}
            >
              {nameLabel}
            </h3>
            {nameBadge && (
              <span
                className="inline-flex items-center rounded-sm text-[11px] font-semibold px-2.5 py-0.5"
                style={{
                  background: `rgba(${pillRgbN}, 0.08)`,
                  border: `1px solid rgba(${pillRgbN}, 0.18)`,
                  color: "rgba(255,255,255,0.8)",
                }}
              >
                {nameBadge === "new" ? "NEW" : "COMING SOON"}
              </span>
            )}
          </div>
        );
      })()}
      {pillar.tagline && (
        <p className="text-[13px] leading-normal mb-4 m-0 text-grey-400">
          {pillar.tagline}
        </p>
      )}
      {pillar.pills && pillar.pills.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-auto">
          {pillar.pills.map((pill, i) => {
            const { label, badge } = parsePill(pill);
            const pillColor = color ?? "#F74B48";
            const pillRgb = rgb ?? "247, 75, 72";
            return (
              <span
                key={i}
                className="inline-flex items-center rounded-sm overflow-hidden"
                style={{
                  background: `rgba(${pillRgb}, 0.08)`,
                  border: `1px solid rgba(${pillRgb}, 0.18)`,
                }}
              >
                <span
                  className={`text-[11px] font-semibold py-0.5 ${badge ? "pl-2.5 pr-1.5" : "px-2.5"}`}
                  style={{ color: pillColor }}
                >
                  {label}
                </span>
                {badge && (
                  <>
                    <span
                      className="self-stretch w-px"
                      style={{ background: `rgba(${pillRgb}, 0.3)` }}
                    />
                    <span
                      className="text-[11px] font-semibold pl-1.5 pr-2.5 py-0.5"
                      style={{ color: "rgba(255,255,255,0.8)" }}
                    >
                      {badge === "new" ? "NEW" : "COMING SOON"}
                    </span>
                  </>
                )}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );

  if (pillar.link) {
    return <Link href={pillar.link ?? "#"} className="block">{inner}</Link>;
  }
  return <div className="block">{inner}</div>;
}

function FoundationBand({ foundation }: { foundation: Foundation }) {
  const colorValue = (
    typeof foundation.color === "object" && foundation.color !== null
      ? (foundation.color as any).value
      : foundation.color
  ) || null;
  const rgb = colorValue ? extractRgb(colorValue) : null;

  const inner = (
    <div
      className="relative overflow-hidden flex flex-col md:flex-row gap-6 md:gap-12 items-start md:items-center rounded-lg bg-grey-900 mt-2 p-5 md:p-8"
      style={rgb ? { border: `1px solid rgba(${rgb}, 0.3)` } : undefined}
    >
      <div
        className="absolute top-0 left-0 right-0 h-[3px]"
        style={
          colorValue
            ? { background: colorValue }
            : { backgroundImage: "url('/images/footer_gradient-low-res.webp')", backgroundSize: "cover", backgroundPosition: "center" }
        }
      />
      <div className="w-full md:min-w-[220px] md:w-auto">
        {foundation.superTag && (
          <p className="text-xxs font-mono text-grey-600 font-bold tracking-[0.14em] uppercase mb-2.5 m-0">
            {foundation.superTag}
          </p>
        )}
        {foundation.name && (() => {
          const { label: fNameLabel, badge: fNameBadge } = parsePill(foundation.name);
          const fRgb = rgb ?? "247, 75, 72";
          return (
            <div className="flex items-center gap-2 mb-2">
              <h3
                className="text-2xl font-extrabold text-white m-0 leading-none"
                style={{ letterSpacing: "-0.03em", fontFamily: "var(--font-roobert)" }}
              >
                {fNameLabel}
              </h3>
              {fNameBadge && (
                <span
                  className="inline-flex items-center rounded-sm text-[11px] font-semibold px-2.5 py-0.5"
                  style={{
                    background: `rgba(${fRgb}, 0.08)`,
                    border: `1px solid rgba(${fRgb}, 0.18)`,
                    color: "rgba(255,255,255,0.8)",
                  }}
                >
                  {fNameBadge === "new" ? "NEW" : "COMING SOON"}
                </span>
              )}
            </div>
          );
        })()}
        {foundation.description && (
          <p className="text-[13.5px] leading-[1.6] m-0 text-grey-400 max-w-[30ch]">
            {foundation.description}
          </p>
        )}
        {foundation.footnote && (() => {
          const { label: fLabel, badge: fBadge } = parsePill(foundation.footnote);
          const fRgb = rgb ?? "247, 75, 72";
          const fColor = colorValue ?? "#F74B48";
          return (
            <div className="flex items-center gap-2 mt-2.5">
              <span className="text-[12px] font-semibold" style={{ color: fColor }}>
                {fLabel}
              </span>
              {fBadge && (
                <span
                  className="inline-flex items-center rounded-sm overflow-hidden"
                  style={{
                    background: `rgba(${fRgb}, 0.08)`,
                    border: `1px solid rgba(${fRgb}, 0.18)`,
                  }}
                >
                  <span
                    className="text-[11px] font-semibold px-2.5 py-0.5"
                    style={{ color: "rgba(255,255,255,0.8)" }}
                  >
                    {fBadge === "new" ? "NEW" : "COMING SOON"}
                  </span>
                </span>
              )}
            </div>
          );
        })()}
      </div>

      {foundation.pillGroups && foundation.pillGroups.length > 0 && (
        <div className="flex flex-col gap-4">
          {foundation.pillGroups.map((group, i) => (
            <div key={group._key ?? i}>
              {group.label && (
                <p
                  className="text-xxs font-bold tracking-[0.12em] uppercase mb-2 m-0"
                  style={{ color: "rgba(255,255,255,0.3)", fontFamily: "var(--type-mono)" }}
                >
                  {group.label}
                </p>
              )}
              <div className="flex flex-wrap gap-1.5">
                {(group.pills ?? []).map((pill, j) => {
                  const { label: pLabel, badge: pBadge } = parsePill(pill);
                  const fRgb = rgb ?? "247, 75, 72";
                  return (
                    <span
                      key={j}
                      className="inline-flex items-center rounded-sm overflow-hidden"
                      style={{
                        background: `rgba(${fRgb}, 0.08)`,
                        border: `1px solid rgba(${fRgb}, 0.18)`,
                      }}
                    >
                      <span
                        className={`text-[11px] font-semibold py-0.5 ${pBadge ? "pl-2.5 pr-1.5" : "px-2.5"}`}
                        style={{ color: colorValue ?? undefined }}
                      >
                        {pLabel}
                      </span>
                      {pBadge && (
                        <>
                          <span className="self-stretch w-px" style={{ background: `rgba(${fRgb}, 0.3)` }} />
                          <span
                            className="text-[11px] font-semibold pl-1.5 pr-2.5 py-0.5"
                            style={{ color: "rgba(255,255,255,0.8)" }}
                          >
                            {pBadge === "new" ? "NEW" : "COMING SOON"}
                          </span>
                        </>
                      )}
                    </span>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (foundation.link) {
    return <Link href={foundation.link ?? "#"}>{inner}</Link>;
  }
  return <div>{inner}</div>;
}

export default function ArchitecturePillars({
  tag,
  heading,
  subHeading,
  pillars,
  foundation,
  bottomText,
  bottomTextContent,
  paddingTop = "none",
  paddingBottom = "none",
}: ArchitecturePillarsProps) {
  const ptClass = `section-pt-${paddingTop ?? "none"}`;
  const pbClass = `section-pb-${paddingBottom ?? "none"}`;

  return (
    <section className={`w-full ${ptClass} ${pbClass}`}>
      <div className="container mx-auto px-4 xl:px-[54px]">
        {(tag || heading || subHeading) && (
          <div className="text-center max-w-[640px] mx-auto mb-12">
            {tag && (
              <div className="flex justify-center mb-6">
                <Tag variant="content">{tag}</Tag>
              </div>
            )}
            {heading && (
              <h2
                className="text-white font-normal m-0 leading-[130%] text-[36px]"
                style={{ fontFamily: "var(--font-roobert)" }}
              >
                <GradientTextParser text={heading} />
              </h2>
            )}
            {subHeading && (
              <p className="text-base leading-6 m-0 mt-4 text-grey-600">
                {subHeading}
              </p>
            )}
          </div>
        )}

        <div className="max-w-7xl mx-auto w-full">
          {pillars && pillars.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {pillars.map((pillar, i) => (
                <PillarCard key={pillar._key ?? i} pillar={pillar} />
              ))}
            </div>
          )}

          {foundation && pillars && pillars.length > 0 && (
            <div className="hidden md:grid grid-cols-3 gap-4 mt-2">
              {pillars.map((pillar, i) => {
                const pillarColor = (
                  typeof pillar.color === "object" && pillar.color !== null
                    ? (pillar.color as any).value
                    : pillar.color
                ) || null;
                const pillarRgb = pillarColor ? extractRgb(pillarColor) : null;
                return (
                  <div key={i} className="flex justify-center">
                    <span
                      className="text-2xl leading-none text-grey-600"
                    >
                      ↓
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          {foundation && <FoundationBand foundation={foundation} />}

          {(bottomTextContent || !!bottomText?.length) && (
            <div className="text-center mt-10 md:mt-16 [&>p]:m-0 [&>p]:text-[16px] [&>p]:text-grey-550">
              {bottomTextContent ?? (
                <PortableText value={bottomText!} components={bottomTextComponents} />
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
